// Mint-Payroll: bảng lương kỳ (chỉ xem). Dữ liệu mẫu và công thức theo code board (đơn vị: triệu ₫).
// Gross = lương chính theo công + trách nhiệm + (phụ cấp + kiêm nhiệm nếu có công) + lương chặng + OT + KPI + thưởng + chuyên cần;
// BH NLĐ = 10,5% căn cứ; TNCN lũy tiến sau giảm trừ bản thân 15,5 và người phụ thuộc 6,2.

/** user, ghế, công, chuẩn, lương chính, trách nhiệm, phụ cấp, kiêm nhiệm, OT, KPI, căn cứ BH, người phụ thuộc, tạm ứng, khác */
export type PayRow = readonly [string, string, number, number, number, number, number, number, number, number, number, number, number, number];

export const PAYROLL_ROWS: readonly PayRow[] = [
  ['DAVID', 'SALE-MGR + BOD-DDIR', 22, 22, 28, 6, 2.2, 3, 0, 4.2, 28, 0, 3, 0],
  ['PHONGNT', 'TRD-MGR + OPS-MGR', 22, 22, 22, 5, 2.4, 2.5, 1.6, 3.1, 22, 0, 2, 0],
  ['HANHLTM', 'ACC-CP + HRD-MGR', 22, 22, 18, 3.6, 1.7, 3.6, 0, 2.4, 18, 0, 0, 0],
  ['PhuongHT', 'CSD-BKG · L03', 21.5, 22, 11, 1, 1, 0, 0.4, 1.3, 11, 0, 0, 0],
  ['NhiTHT', 'CSD-BKG · L03', 22, 22, 11, 1, 1, 0, 0, 1.2, 11, 0, 0, 0],
  ['MAITTN', 'CSD-CSO · L03', 21, 22, 10, 0.5, 1, 0, 0.3, 1.1, 10, 0, 0, 0.2],
  ['THANHTTK', 'TRD-DSP · L03', 22, 22, 10.5, 1, 1.5, 0, 1.9, 1.1, 10.5, 0, 1, 0],
  ['TUANNT', 'SALE-SE · L04', 20, 22, 12, 0.5, 1.2, 0, 0, 1.5, 12, 0, 0, 0],
  ['LIENNTK', 'ACC-AR · L04', 22, 22, 13, 1, 1, 0, 0, 1.2, 13, 0, 0, 0],
  ['KHAINH', 'TRD-DRV · L02', 22, 22, 7, 0, 1.9, 0, 0.9, 0.6, 7, 0, 0.5, 0],
  ['HAI•••', 'TRD-DRV · L02', 22, 22, 7, 0, 1.9, 0, 0.6, 0.5, 7, 0, 0, 0],
  ['CongNV', 'TRD-DRV · L02', 0, 22, 7, 0, 1.9, 0, 0, 0, 7, 0, 0, 0],
];

/** Lương chặng (lệnh vận tải Finish → khoản trong lương tháng), theo người. */
const LEG: Record<string, number> = { 'HAI•••': 0.4 + 0.7 };
/** Thưởng nhập tay (chịu thuế). */
const BONUS: Record<string, number> = { NhiTHT: 0.5, THANHTTK: 0.4 };
const ATT = 0.3;
const NO_ATT = ['TUANNT', 'MAITTN', 'CongNV', 'PhuongHT'];
const BRACKETS: readonly (readonly [number, number])[] = [[10, 0.05], [30, 0.1], [60, 0.2], [100, 0.3], [1e9, 0.35]];

export function pit(x: number): number {
  let prev = 0;
  let tax = 0;
  for (const [cap, r] of BRACKETS) {
    if (x > prev) tax += (Math.min(x, cap) - prev) * r;
    prev = cap;
  }
  return tax;
}

export interface PayCalc {
  u: string; seat: string; cong: number; std: number;
  main: number; alw: number; resp: number; leg: number; ot: number; kpi: number; bonus: number;
  gross: number; ins: number; pit: number; adv: number; oth: number; net: number;
}

export function calcPay(r: PayRow): PayCalc {
  const [u, seat, cong, std, base, resp, alw, duty, ot, kpi, si, dep, adv, oth] = r;
  const leg = LEG[u] ?? 0;
  const bonusT = BONUS[u] ?? 0;
  const att = cong && !NO_ATT.includes(u) ? ATT : 0;
  const main = std ? (base * cong) / std : 0;
  const alwAll = cong ? alw + duty : 0;
  const gross = main + resp + alwAll + leg + ot + kpi + bonusT + att;
  const ins = cong ? si * 0.105 : 0;
  const taxable = Math.max(0, gross - ins - 15.5 - dep * 6.2 - Math.min(0.73, cong ? alw : 0));
  const tax = pit(taxable);
  return { u, seat, cong, std, main, alw: alwAll, resp, leg, ot, kpi, bonus: bonusT + att, gross, ins, pit: tax, adv, oth, net: gross - ins - tax - adv - oth };
}

export function calcAll(rows: readonly PayRow[] = PAYROLL_ROWS): PayCalc[] {
  return rows.map(calcPay);
}

export const nf = (x: number): string => (Math.round(x * 10) / 10).toFixed(1).replace('.', ',');
export const fmt = (v: number, neg = false): string => (v ? (neg ? '−' : '') + nf(v) : '—');

export const DEPTS: Record<string, readonly [string, string]> = {
  SALE: ['Kinh doanh', 'var(--d-blue)'],
  CSD: ['Dịch vụ KH · Booking', 'var(--d-amber)'],
  TRD: ['Vận tải', 'var(--d-green)'],
  ACC: ['Kế toán', 'var(--d-violet)'],
};

/** Quỹ lương theo phòng: gom Gross theo mã ghế đầu. */
export function byDept(rows: readonly PayCalc[]): { k: string; c: string; v: number; n: number }[] {
  const g: Record<string, { v: number; n: number }> = {};
  for (const c of rows) {
    const d = c.seat.split('-')[0] ?? '';
    const key = DEPTS[d] ? d : 'OTH';
    g[key] = g[key] ?? { v: 0, n: 0 };
    g[key].v += c.gross;
    g[key].n += 1;
  }
  return Object.keys(g)
    .map((k) => ({ k: (DEPTS[k] ?? ['Khác'])[0], c: (DEPTS[k] ?? ['', 'var(--d-gray)'])[1], v: g[k]!.v, n: g[k]!.n }))
    .sort((a, b) => b.v - a.v);
}

export interface PayWarn { k: string; sub: string; tone: 'err' | 'warn' }
export const PAYROLL_WARNS: readonly PayWarn[] = [
  { k: 'CongNV · công thực tế 0/22', sub: 'Chưa có bảng công hoặc nghỉ cả kỳ · phiếu tính 0, kiểm trước khi duyệt', tone: 'err' },
  { k: 'ThaoTN · thiếu lương chuẩn TRD-DRV × L01', sub: 'Không có dòng bảng lương chuẩn → bỏ qua khi tính · thêm ở tab Bảng lương chuẩn', tone: 'warn' },
  { k: 'MAITTN · căn cứ BHXH 10,0 tr', sub: 'Kiểm lại so với lương tối thiểu vùng I và mức đóng của bậc L03', tone: 'warn' },
];

export const PAY_PERIODS: readonly (readonly [string, string, 'ok' | 'warn' | 'mute'])[] = [
  ['11/2026', 'Chưa chốt công', 'mute'], ['10/2026', 'Calculated', 'warn'], ['09/2026', 'Đã gửi phiếu', 'ok'], ['08/2026', 'Đã gửi phiếu', 'ok'], ['07/2026', 'Đã gửi phiếu', 'ok'],
];

export const PAY_STEPS: readonly (readonly [string, string])[] = [
  ['Chốt công', 'Công tháng đã chốt'],
  ['Tính lương', 'Gross → BH → TNCN → Net'],
  ['KTT kiểm', 'Ghế trống → PHILIP'],
  ['GĐ duyệt', '[BOD-DIR] PHILIP'],
  ['Đã trả', 'ngày chi · tài khoản'],
  ['Gửi phiếu', 'email từng người'],
];

/** Bảng lương chuẩn chức danh × bậc (tr ₫): mã ghế, bậc, tên, lương vị trí, căn cứ BHXH, phụ cấp chuẩn, Gross mục tiêu, đang áp. Theo `STD0` của board. */
export type StdRow = readonly [string, string, string, number, number, number, number, number];
export const STD_ROWS: readonly StdRow[] = [
  ['SALE-SE', 'L02', 'Nhân viên Kinh doanh', 9, 9, 1.5, 11, 1],
  ['SALE-SE', 'L03', 'Nhân viên Kinh doanh', 10.5, 10.5, 1.7, 12.8, 2],
  ['SALE-SE', 'L04', 'Nhân viên Kinh doanh', 12, 12, 1.7, 14.5, 1],
  ['SALE-MGR', 'L07', 'Trưởng phòng Kinh doanh', 28, 28, 2.2, 36, 1],
  ['CSD-BKG', 'L01', 'Nhân viên Đặt chỗ', 7.5, 7.5, 1, 8.5, 0],
  ['CSD-BKG', 'L02', 'Nhân viên Đặt chỗ', 9, 9, 1, 10.3, 1],
  ['CSD-BKG', 'L03', 'Nhân viên Đặt chỗ', 11, 11, 1, 13, 3],
  ['CSD-BKG', 'L04', 'Nhân viên Đặt chỗ', 12.5, 12.5, 1, 14.6, 0],
  ['CSD-CSO', 'L02', 'Nhân viên DVKH', 9.5, 9.5, 1, 10.8, 1],
  ['CSD-CSO', 'L03', 'Nhân viên DVKH', 10, 10, 1, 11.6, 3],
  ['TRD-DSP', 'L03', 'Nhân viên Điều độ', 10.5, 10.5, 1.5, 12.5, 1],
  ['TRD-DRV', 'L02', 'Tài xế', 7, 7, 1.9, 9.5, 6],
  ['ACC-CP', 'L05', 'Kế toán chi phí', 18, 18, 1.7, 22, 1],
  ['ACC-AR', 'L04', 'Kế toán bán hàng — công nợ', 13, 13, 1, 15, 1],
];
export const STD_EFF = '01/Jan/2026';
export const STD_DEPTS = ['Tất cả', 'SALE', 'CSD', 'TRD', 'ACC'] as const;

/** Tab Tham số & biểu thuế: mã, tên, giá trị, đơn vị, hiệu lực, căn cứ, trạng thái. Theo `PAR` của board. */
export type ParRow = readonly [string, string, string, string, string, string, 'Confirmed' | 'NeedVerify'];
const NQ = 'NQ 110/2025/UBTVQH15';
const BHXH = 'Luật BHXH 2024';
const QC = 'Quy chế lương công ty';
export const PAR_ROWS: readonly ParRow[] = [
  ['PIT_SELF-2026', 'Giảm trừ gia cảnh bản thân', '15.500.000', 'VND/tháng', '01/Jan/2026', NQ, 'Confirmed'],
  ['PIT_DEPENDENT-2026', 'Giảm trừ người phụ thuộc', '6.200.000', 'VND/tháng', '01/Jan/2026', NQ, 'Confirmed'],
  ['SI_EMP_BHXH', 'BHXH phần người lao động', '8', '%', '01/Jul/2025', BHXH, 'Confirmed'],
  ['SI_EMP_BHYT', 'BHYT phần người lao động', '1,5', '%', '01/Jul/2025', BHXH, 'Confirmed'],
  ['SI_EMP_BHTN', 'BHTN phần người lao động', '1', '%', '01/Jul/2025', BHXH, 'Confirmed'],
  ['SI_COM_BHXH', 'BHXH phần công ty', '17', '%', '01/Jul/2025', BHXH + ' · QĐ 595/QĐ-BHXH', 'Confirmed'],
  ['SI_COM_OSH', 'Quỹ TNLĐ-BNN', '0,5', '%', '01/Jul/2025', 'QĐ 595/QĐ-BHXH', 'Confirmed'],
  ['UNION_RATE-2025', 'Kinh phí công đoàn', '2', '%', '01/Jan/2025', 'Luật Công đoàn', 'Confirmed'],
  ['MIN_WAGE_R1-2026', 'Lương tối thiểu vùng I', '5.310.000', 'VND/tháng', '01/Jan/2026', 'NĐ 293/2025/NĐ-CP', 'Confirmed'],
  ['SI_CAP_MULT-2025', 'Trần căn cứ BHXH', '20', 'lần mức tham chiếu', '01/Jul/2025', BHXH, 'Confirmed'],
  ['MUC_THAM_CHIEU-2026H1', 'Mức tham chiếu', '2.340.000', 'VND/tháng', '01/Jan/2026', BHXH, 'NeedVerify'],
  ['RESP_DUTY_MENTOR-2026', 'Phụ cấp kèm cặp người mới', '500.000', 'VND/tháng', '01/Jan/2026', QC, 'NeedVerify'],
  ['RESP_DUTY_OSH-2026', 'Phụ cấp an toàn vệ sinh viên', '300.000', 'VND/tháng', '01/Jan/2026', QC, 'NeedVerify'],
];

/** Biểu thuế TNCN lũy tiến từng phần (số minh hoạ theo board, chờ chốt văn bản pháp lý). */
export const TAX_ROWS: readonly (readonly [number, string, string])[] = BRACKETS.map(([cap, r], i) => {
  const lo = i ? nf(BRACKETS[i - 1]![0]).replace(',0', '') : '0';
  return [i + 1, lo + (cap > 1e8 ? ' tr trở lên' : ' – ' + nf(cap).replace(',0', '') + ' tr'), Math.round(r * 100) + '%'] as const;
});

/** Loại phụ cấp: mã, tên, mức chuẩn, tính BHXH, miễn thuế, ghi chú. */
export type AlwRow = readonly [string, string, string, boolean, boolean, string];
export const ALW_ROWS: readonly AlwRow[] = [
  ['ALLOW_LUNCH-2026', 'Ăn trưa', '0,73 tr', false, true, 'Miễn thuế trong mức quy định'],
  ['ALLOW_PHONE-2026', 'Điện thoại', '0,3–0,5 tr', false, false, 'Theo chức danh'],
  ['ALLOW_FUEL-2026', 'Xăng xe', '0,5–1,2 tr', false, false, 'Sale, hiện trường, tài xế'],
  ['RESP', 'Trách nhiệm', 'theo bảng chuẩn', true, false, 'Nằm trong căn cứ BHXH'],
  ['DUTY_CONCURRENT', 'Phụ cấp kiêm nhiệm', 'kiêm nhiệm', false, false, 'Cộng tự động từ QĐ kiêm nhiệm còn hiệu lực'],
  ['RESP_DUTY_MENTOR-2026', 'Kèm cặp người mới', '0,5 tr', false, false, 'NeedVerify ở tab Tham số'],
];

/** Quy tắc KPI lương: chức danh, bậc, chỉ số, nguồn, cách tính, kỳ. */
export const KPI_RULES: readonly (readonly [string, string, string, string, string, string])[] = [
  ['CSD-BKG', 'L01–L04', 'B_CFM · BK Confirmed trước Close Doc', 'BOOKING · % · stamp', 'Theo % đạt', 'Tháng'],
  ['CSD-CSO', 'L02–L05', 'C_TASK · Task FBL/SNO/PSI/RMB/MBL đúng hạn', 'CS · % · tasks', 'Theo % đạt', 'Tháng'],
  ['TRD-DSP', 'L02–L04', 'D_TASK · Gán xe / Finish đúng hạn', 'DISPATCH · % · tasks', 'Theo % đạt', 'Tháng'],
  ['TRD-DRV', 'L01–L03', 'R_TRIPS · Số chuyến chạy', 'DRIVER · n · trf', 'Theo đơn vị', 'Tháng'],
  ['ACC-CP', 'L03–L06', 'A_PAY · Thanh toán đối tác đúng hạn', 'ACC · % · pay', 'Theo % đạt', 'Quý'],
];
export const ATT_ROWS: readonly (readonly [string, string])[] = [
  ['Đi muộn tối đa (G_LATE)', '2 lần/tháng'], ['Không nghỉ không lương', 'bắt buộc'], ['Mức thưởng', '0,3 tr/tháng'], ['Áp cho', 'mọi người trừ NoClockRequired'],
];

/** Nhật ký gửi phiếu: kỳ 09/2026 (9 người đầu, người thứ 8 lỗi); kỳ 10/2026 chưa gửi (bước hiện tại = KTT kiểm). */
export type LogRow = readonly [string, string, string, string, 'Đã gửi' | 'Lỗi', string];
export const LOG_PERIODS = ['10/2026', '09/2026'] as const;
export function logRows(per: string): LogRow[] {
  if (per !== '09/2026') return [];
  return calcAll().slice(0, 9).map((c, i) => [c.u, c.seat, per, `05/Oct/2026 09:0${i % 10}`, i === 7 ? 'Lỗi' : 'Đã gửi', i === 7 ? 'Hộp thư đầy (552) · thử lại' : ''] as const);
}
